/**
 * Watchlist — port of app/callbacks/misc.update_wl + wl-list rendering.
 * Live prices auto-refresh every 30s per symbol. Clicking a row sets
 * focusSymbol (matches the Dash wl_click callback).
 */
import { useState, useEffect } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useStore } from '@/store';
import { api } from '@/lib/api';
import type { Quote } from '@/types';

function WatchRow({ sym }: { sym: string }) {
  const focus       = useStore(s => s.focusSymbol);
  const setFocus    = useStore(s => s.setFocus);
  const removeWatch = useStore(s => s.removeWatch);
  const setQuote    = useStore(s => s.setQuote);

  const { data, isError } = useQuery<Quote>({
    queryKey: ['wl-quote', sym],
    queryFn:  () => api(`/api/quote/${encodeURIComponent(sym)}`),
    refetchInterval: 30_000,
    staleTime:       30_000,
    retry: false,   // don't retry on 404 NO_QUOTE_DATA
  });

  // Mirror REST quotes into the shared store so Movers (which ranks off
  // store.quotes) always has data — independent of the live WS feed, which
  // can be sparse (uncovered futures, market-closed gaps). The WS overlays
  // fresher ticks on top when it's flowing.
  useEffect(() => {
    if (data && Number.isFinite(data.last ?? data.mid)) setQuote(data);
  }, [data, setQuote]);

  const isActive = sym === focus;
  const hasData = !isError && data && Number.isFinite(data.last ?? data.mid);
  const px  = hasData ? (data!.last ?? data!.mid) : null;
  // A quote can carry a valid price and NO change field. `?? 0` then painted a
  // green "+0.00%" -- a claim the name is unchanged today. Unknown stays null,
  // which the render below already shows as blank/uncoloured.
  const pct = hasData && Number.isFinite(data!.change_pct as number) ? (data!.change_pct as number) : null;
  const upDown = pct === null ? '' : pct >= 0 ? 'wl-chg-up' : 'wl-chg-dn';

  return (
    <div
      className={`wl-item${isActive ? ' active' : ''}`}
      onClick={() => setFocus(sym)}
    >
      <div>
        <div className="wl-sym">{sym}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="wl-px">{px !== null ? `$${px.toFixed(2)}` : '—'}</div>
        <div className={upDown || 'wl-px'}>
          {pct !== null ? `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%` : '—'}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); removeWatch(sym); }}
        style={{
          background: 'none', border: 'none', color: '#333',
          padding: '0 0 0 6px', fontSize: 13, cursor: 'pointer',
        }}
      >×</button>
    </div>
  );
}

export function Watchlist() {
  const watchlist = useStore(s => s.watchlist);
  const addWatch  = useStore(s => s.addWatch);
  const [draft, setDraft] = useState('');

  const submit = () => {
    const s = draft.trim().toUpperCase();
    if (s) { addWatch(s); setDraft(''); }
  };

  // Prefetch all quotes once on mount to seed the cache faster.
  useQueries({
    queries: watchlist.map(sym => ({
      queryKey: ['wl-quote', sym],
      queryFn:  () => api<Quote>(`/api/quote/${encodeURIComponent(sym)}`),
      refetchInterval: 30_000,
      staleTime:       30_000,
    })),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div className="pane-hdr">
        <span>WATCHLIST</span>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="Add ticker"
          style={{ width: 90, fontSize: 10, padding: '2px 5px', height: 18 }}
        />
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {watchlist.map(sym => <WatchRow key={sym} sym={sym} />)}
      </div>
    </div>
  );
}
